import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { InputDecimal } from "../ui/input-decimal";
import { Banknote, Calculator, DollarSign, Keyboard, RotateCcw } from "lucide-react";

import ContagemNotas, { MOEDAS, NOTAS, contarPecas, somaContagem } from "../../features/caixa/abertura/ContagemNotas";
import { reais } from "../../features/caixa/lib/caixa";

function Aba({ ativa, onClick, icone: Icone, children, e2e }) {
  return (
    <button type="button" onClick={onClick} data-e2e={e2e}
      className={`flex-1 h-10 text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-1.5 ${ativa ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
      <Icone className="w-4 h-4" />{children}
    </button>
  );
}

// Abertura do caixa: o dinheiro que fica na gaveta para o troco. Dá para
// digitar o valor ou contar nota por nota (ver features/caixa/abertura).
export default function AbrirCaixaModal({ open, onOpenChange, onAbrir }) {
  const [aba, setAba] = useState("total"); // "total" | "contagem"
  const [valorTotal, setValorTotal] = useState("");
  const [contagem, setContagem] = useState({});
  const [loading, setLoading] = useState(false);

  const somaNotas = somaContagem(contagem);
  const valorFinal = aba === "total" ? Number(valorTotal || 0) : somaNotas;
  const notas = contarPecas(contagem, NOTAS);
  const moedas = contarPecas(contagem, MOEDAS);

  const handleAbrir = async () => {
    setLoading(true);
    try {
      await onAbrir(valorFinal);
      setValorTotal("");
      setContagem({});
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.message || "Erro ao abrir caixa");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 gap-0 overflow-hidden" data-e2e="abrir-caixa-janela">
        <DialogHeader className="px-5 pt-5 pb-3 space-y-1 text-left">
          <DialogTitle className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><DollarSign className="w-4 h-4" /></span>
            Abrir Caixa
          </DialogTitle>
          <p className="text-xs text-muted-foreground">Quanto tem de troco na gaveta agora?</p>
        </DialogHeader>

        <div className="px-5">
          <div className="flex gap-1 bg-muted rounded-xl p-1">
            <Aba ativa={aba === "total"} onClick={() => setAba("total")} icone={Keyboard} e2e="aba-total">Digitar valor</Aba>
            <Aba ativa={aba === "contagem"} onClick={() => setAba("contagem")} icone={Calculator} e2e="aba-contagem">Contar notas</Aba>
          </div>
        </div>

        <div className="px-5 py-4 max-h-[min(58vh,520px)] overflow-y-auto">
          {aba === "total" ? (
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="valor-abertura">Valor de abertura</label>
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-muted-foreground">R$</span>
                <InputDecimal id="valor-abertura" decimals={2} value={valorTotal} onChange={setValorTotal}
                  className="h-14 text-2xl font-bold" autoFocus data-e2e="valor-abertura" />
              </div>
              <p className="text-xs text-muted-foreground">Digite só os números: 15000 vira R$ 150,00.</p>
            </div>
          ) : (
            <ContagemNotas qtd={contagem} setQtd={setContagem} />
          )}
        </div>

        <div className="px-5 pt-3 pb-5 border-t bg-muted/30 space-y-3">
          <div className="flex items-end justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Total na gaveta</p>
              {aba === "contagem" ? (
                <p className="text-[11px] text-muted-foreground flex items-center gap-2" data-e2e="contagem-pecas">
                  <Banknote className="w-3.5 h-3.5" />
                  {notas} {notas === 1 ? "nota" : "notas"} · {moedas} {moedas === 1 ? "moeda" : "moedas"}
                  {notas + moedas > 0 ? (
                    <button type="button" onClick={() => setContagem({})} className="inline-flex items-center gap-0.5 text-primary font-semibold">
                      <RotateCcw className="w-3 h-3" /> Zerar
                    </button>
                  ) : null}
                </p>
              ) : null}
            </div>
            <span className="text-3xl font-black tabular-nums text-primary" data-e2e="abrir-caixa-total">{reais(valorFinal)}</span>
          </div>
          <Button onClick={handleAbrir} disabled={loading} className="w-full h-12 text-base font-bold" data-e2e="abrir-caixa-confirmar">
            {loading ? "Abrindo..." : `Abrir caixa com ${reais(valorFinal)}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
